/* eslint-disable no-use-before-define */
import React from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classnames from 'classnames';
import {List, Map} from 'immutable';
import DateTimePicker from 'generic/DateTimePicker/DateTimePicker.react';
import NumberInput from 'client/generic/Forms/NumberInput/NumberInput.react';
import {NumberField, Text} from '@albert-io/atomic';
import Checkbox from 'generic/Forms/Checkbox/Checkbox.react';
import {debouncedHandleInputChange} from '../../shared/utils';
import timeLimitTooltip from '../../shared/TimeLimitTooltip.react';
import {convertDateAndTimeToMomentObject} from 'lib/timeUtils';

import withTracking from 'lib/hocs/withTracking';

import {callAction} from 'client/framework';
import createAssignmentActions from '../../CreateAssignment.actions';

const TrackedNumberInput = withTracking(NumberField, {
  onFocus: {event: 'click', type: 'input', content: () => 'Time limit'}
});

export default class CreateAssignmentDateTimeOptions extends React.Component {
  static propTypes = {
    dueDate: PropTypes.string.isRequired,
    errorData: ImmutablePropTypes.map.isRequired,
    onChange: PropTypes.func.isRequired,
    timeLimit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sections: PropTypes.instanceOf(List)
  };

  getTimeLimitField() {
    if (!this.props.sections.isEmpty()) {
      return <SectionTimeLimitEditor sections={this.props.sections} />;
    }
    return (
      <div>
        <Text bold size='s' emphasis='regular'>
          Time limit (minutes){timeLimitTooltip}
        </Text>
        <TrackedNumberInput
          border
          className='acf__timelimit-input'
          error={this.props.errorData.has('time_limit')}
          message={this.props.errorData.get('time_limit')}
          onFocus={() => {}}
          min={0}
          name='time_limit'
          onChange={this.props.onChange}
          placeholder='No time limit'
          defaultValue={this.props.timeLimit}
        />
      </div>
    );
  }

  render() {
    return (
      <div className='acf__options-row'>
        <DateTimePicker
          className='u-mar-b_1 u-mar-r_1'
          customDefaultTime='23:59'
          dateLabel='Due Date'
          defaultValue={this.props.dueDate}
          dateError={this.props.errorData.get('due_date', null)}
          onChange={({date, time}) => {
            const dueDate = convertDateAndTimeToMomentObject({date, time});
            debouncedHandleInputChange('due_date', dueDate);
          }}
          required
          timeLabel='Due Time'
        />
        <div
          className={classnames('acf__input-wrapper acf__input-wrapper--timelimit', {
            'acf__input-wrapper--section-time-limits': !this.props.sections.isEmpty()
          })}
        >
          {this.getTimeLimitField()}
        </div>
      </div>
    );
  }
}

/**
 * SectionTimeLimitEditor
 * ===
 *
 * This component manages its state internally so we can do two things more easily:
 * - Prevent the sections in our store and the values here from falling out of sync
 * - Reset the input to its default when the user opts for the default time over custom time
 *
 * The idea is to use component state to force updates on all children when needed, and to
 * dispatch the relevant changes to our store (and to validate) on every componentDidUpdate,
 * like any other side effect.
 */
class SectionTimeLimitEditor extends React.Component {
  static propTypes = {
    sections: ImmutablePropTypes.list
  };

  constructor(props) {
    super(props);

    // Bootstrap the component state from what the store provides to us in our props
    this.state = {
      sections: props.sections.reduce((prev, curr) => {
        return prev.set(
          curr.getPosition(),
          Map({
            default: curr.getTimeLimit(),
            section: curr,
            custom: false
          })
        );
      }, Map())
    };
  }

  componentDidUpdate(_, prevState) {
    if (this.state.sections !== prevState.sections) {
      const payload = this.state.sections.valueSeq().map((entry) => entry.get('section'));
      callAction(createAssignmentActions.UPDATE_SECTIONS, payload);
      callAction(createAssignmentActions.VALIDATE_ASSIGNMENT);
    }
  }

  handleCheckboxChange = (e, sectionToUpdate) => {
    const {checked} = e.target;
    const path = sectionToUpdate.getPosition();
    this.setState(({sections}) => {
      return {
        sections: sections.update((prevSections) => {
          let updatedSections = prevSections.setIn([path, 'custom'], checked);
          if (checked) {
            updatedSections = updatedSections.setIn([path, 'section'], sectionToUpdate);
          } else {
            const value = prevSections.getIn([path, 'default']);
            updatedSections = updatedSections.setIn(
              [path, 'section'],
              sectionToUpdate.setTimeLimit(value)
            );
          }
          return updatedSections;
        })
      };
    });
  };

  handleInputChange = (e, sectionToUpdate) => {
    const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
    this.setState(({sections}) => {
      return {
        sections: sections.setIn(
          [sectionToUpdate.getPosition(), 'section'],
          sectionToUpdate.setTimeLimit(value)
        )
      };
    });
  };

  render() {
    return (
      <fieldset className='u-border_none u-pad_0 u-mar-b_0 u-mar-lr_0 u-mar-t_2'>
        <Text as='legend' bold className='u-mar-b_1' color={null} size='s'>
          Section time limits (minutes)
        </Text>
        {this.state.sections.valueSeq().map((entry) => {
          const section = entry.get('section');
          const value = section.getTimeLimit();
          const error = section.validators.getTimeLimit()?.join(' ');
          const isCustomTimeLimit = entry.get('custom');

          // Keying by position AND checkbox value ensures the inputs remain in sync with what's in the store
          const key = `${section.getPosition()}${isCustomTimeLimit}`;
          return (
            <div key={key} className='acf-section-time-limit'>
              <NumberInput
                autoCorrect
                disabled={!isCustomTimeLimit}
                error={isCustomTimeLimit && !!error}
                errorMessage={error}
                label={
                  <span>
                    {section.getTitle()} {timeLimitTooltip}
                  </span>
                }
                min={1}
                onChange={(e) => this.handleInputChange(e, section)}
                updateInputOnValueMismatch={false}
                value={value}
              />
              <Checkbox
                label='Use custom time'
                checked={isCustomTimeLimit}
                onChange={(e) => this.handleCheckboxChange(e, section)}
              />
            </div>
          );
        })}
      </fieldset>
    );
  }
}
